import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  RewardProgramDto,
  RewardProgramIcon,
  RewardProgramStatus,
  RewardWidgetStatus,
} from '@pages/RewardsHub/RewardHubDto'
import { RewardProgramTierProgressBars } from '@pages/RewardsHub/RewardProgramTierProgressBars'
import { useRewardProgramTierProgress } from '@pages/RewardsHub/hooks/useRewardProgramTierProgress'
import { useRewardProgramWidget } from '@pages/RewardsHub/hooks/useRewardProgramWidget'

import { Body1, Body2, Caption1, Title1 } from '@ui/Typography'
import { Badge } from '@ui/components/Badge/Badge'
import { Button } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { ChevronIcon } from '@ui/icons/Chevron.icon'
import { SpinsIcon } from '@ui/icons/Spins.icon'
import { TrophyIcon } from '@ui/icons/Trophy.icon'
import { TradingViewIcon } from '@ui/icons/platforms/TradingView.icon'

import { AnimatedHeight } from '@shared/animations/AnimatedHeight'
import { Box } from '@shared/components/Box'

import styles from './RewardPrograms.module.scss'

interface RewardProgramWidgetProps {
  rewardProgram: RewardProgramDto
}

const iconMapping = [
  { name: RewardProgramIcon.MIXED, icon: TrophyIcon },
  { name: RewardProgramIcon.SPINS, icon: SpinsIcon },
  { name: RewardProgramIcon.TRADING_VIEW, icon: TradingViewIcon },
]

export const RewardProgramWidget: React.FC<RewardProgramWidgetProps> = (
  props: RewardProgramWidgetProps
) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = React.useState(false)

  const {
    isLoading,
    rewardProgramDetails,
    rewardProgramEligibilityCheck,
    timeRemaining,
    isClaiming,
    isProgramInProgress,
    shouldShowClaimButton,
    claimButtonLabel,
    badgeColor,
    badgeLabel,
    claimableTierIndex,
    handleClick,
  } = useRewardProgramWidget(props.rewardProgram)

  const ProgramIcon =
    iconMapping.find((item) => item.name === rewardProgramDetails?.rewardProgramInfo.icon?.id)
      ?.icon ?? TrophyIcon

  const { canExpand, claimedTierName } = useRewardProgramTierProgress({
    rewardProgramTier: rewardProgramDetails?.rewardProgramInfo.tiers,
    progress: rewardProgramEligibilityCheck,
    claimableTierIndex,
    rewardProgram: rewardProgramDetails,
  })

  const clickBtnToNavigate = () => {
    const url = rewardProgramDetails?.rewardProgramInfo.termsAndConditionsUrl
    if (!url) return
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  if (
    isLoading ||
    rewardProgramDetails?.rewardProgramInfo.rewardStatus.id !== RewardProgramStatus.ACTIVE
  ) {
    return null
  }

  return (
    <AnimatedHeight>
      <Box className={styles.rewardProgramItem} flex direction='column' justify='space-between'>
        <Box flex direction='column' gap='x16'>
          <Box flex gap='x4' align='center' justify='space-between'>
            <Box flex gap='x8' align='center'>
              <ProgramIcon />
              <Title1 semibold color='text-high-em'>
                {rewardProgramDetails?.rewardProgramInfo.displayName}
              </Title1>
            </Box>

            <Caption1 color='text-high-em'>
              <Badge size={'sm'} type={badgeColor}>
                {badgeLabel}
              </Badge>
            </Caption1>
          </Box>

          <Body1 color='text-med-em'>
            {rewardProgramEligibilityCheck?.rewardWidgetState.id === RewardWidgetStatus.CLAIMED
              ? claimedTierName + ' ' + t('RewardsHub.Reward Programs.claimed')
              : rewardProgramEligibilityCheck?.rewardWidgetState.id === RewardWidgetStatus.ENDED
              ? t('RewardsHub.Reward Programs.status ended message')
              : rewardProgramDetails?.rewardProgramInfo.description}{' '}
            {rewardProgramEligibilityCheck?.rewardWidgetState.id !== RewardWidgetStatus.ENDED && (
              <>
                <br /> {t('RewardsHub.Reward Programs.Resets in')}
                {': '}
                <strong>
                  {timeRemaining.days}d {timeRemaining.hours}h {timeRemaining.minutes}m
                </strong>
              </>
            )}
          </Body1>

          <RewardProgramTierProgressBars
            rewardProgramTier={rewardProgramDetails?.rewardProgramInfo.tiers}
            progress={rewardProgramEligibilityCheck}
            claimableTierIndex={claimableTierIndex}
            rewardProgram={rewardProgramDetails}
            isExpanded={isExpanded || !canExpand}
          />

          <Box flex align={'center'} justify={'space-between'}>
            <Box flex alignSelf='flex-start' gap={'x12'}>
              <Button.Secondary onClick={clickBtnToNavigate} size='sm'>
                {t('RewardsHub.Learn More')}
              </Button.Secondary>
              {shouldShowClaimButton && (
                <Button.Primary
                  disabled={isProgramInProgress}
                  size='sm'
                  onClick={handleClick}
                  loading={isClaiming}
                >
                  {claimButtonLabel}
                </Button.Primary>
              )}
            </Box>
            {canExpand && (
              <Box gap={'x8'} flex onClick={() => setIsExpanded((prev) => !prev)}>
                <Link>
                  <Body2 color={'text-info-high-em'}>
                    {isExpanded
                      ? t('RewardsHub.Reward Programs.Collapse')
                      : t('RewardsHub.Reward Programs.Expand')}
                  </Body2>
                </Link>
                <ChevronIcon
                  size={24}
                  color={'text-info-high-em'}
                  direction={isExpanded ? 'up' : 'down'}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </AnimatedHeight>
  )
}
